import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ExamTimer } from '../components/ExamTimer';
import QuestionRenderer, { isCorrectAnswer, isAnswerEmpty, isCorrectOption, formatCorrectAnswer, getQuestionTypeLabel } from '../components/QuestionRenderer';
import { useAuthStore } from '../store/authStore';
import { collection, getDocs, query, limit, addDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Question, AnswerValue } from '../types/question';

export default function ExamPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [started, setStarted] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [questionStartAt, setQuestionStartAt] = useState<number | null>(null);
  const [timeSpent, setTimeSpent] = useState<Record<string, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'exam' | 'training'>('training');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [resultStats, setResultStats] = useState<{ correct: number; incorrect: number; unanswered: number; averageSecondsPerQuestion: number } | null>(null);
  const [checkedQuestions, setCheckedQuestions] = useState<Set<string>>(new Set());
  const [progressFilter, setProgressFilter] = useState<'all' | 'incorrect' | 'unanswered' | 'failed'>('all');
  const [tick, setTick] = useState(0);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [practiceFailedOnly, setPracticeFailedOnly] = useState(false);
  const [practiceUnansweredOnly, setPracticeUnansweredOnly] = useState(false);
  const [isProgressCollapsed, setIsProgressCollapsed] = useState(true);
  const [selectedReference, setSelectedReference] = useState<'PMBOK 08' | 'PMBOK 07'>('PMBOK 08');
  const [selectedDifficulty, setSelectedDifficulty] = useState<'all' | 'Hard' | 'Expert' | 'Hyper-Expert'>('all');
  const [selectedDomain, setSelectedDomain] = useState<'all' | 'People' | 'Process' | 'Business Environment'>('all');
  const [selectedType, setSelectedType] = useState<'all' | 'standard'>('all');
  const [overallFailedIds, setOverallFailedIds] = useState<Set<string>>(new Set());
  const [overallUnansweredIds, setOverallUnansweredIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('reference');
    const type = params.get('type');
    const mode = params.get('mode');

    if (ref === 'PMBOK 07' || ref === 'PMBOK 08') {
      setSelectedReference(ref);
    }
    if (type === 'standard' || type === 'all') {
      setSelectedType(type);
    }
    if (mode === 'training' || mode === 'exam') {
      setMode(mode);
    }
  }, [location.search]);

  const currentQuestion = questions[currentIndex];
  const autoStartAttempted = useRef(false);

  // Load attempts and calculate filter counts on component mount and when reference changes
  useEffect(() => {
    if (!user?.firebase_uid) {
      console.log('No user firebase_uid, skipping attempt load');
      return;
    }
    const loadAttempts = async () => {
      try {
        console.log('Loading attempts for user:', user.firebase_uid, 'reference:', selectedReference);
        const attemptsQuery = query(collection(db, 'attempts'), where('userId', '==', user.firebase_uid));
        const attemptsSnap = await getDocs(attemptsQuery);
        const attempts = attemptsSnap.docs.map(d => d.data()) as any[];
        console.log('Total attempts found:', attempts.length);

        // Group by questionId, find latest attempt per question
        const attemptsByQ = new Map<string, any[]>();
        attempts.forEach((a: any) => {
          const qId = a.questionId;
          if (!attemptsByQ.has(qId)) attemptsByQ.set(qId, []);
          attemptsByQ.get(qId)!.push(a);
        });
        console.log('Unique questions attempted:', attemptsByQ.size);

        const failedIds = new Set<string>();
        const attemptedQIds = new Set<string>();
        attemptsByQ.forEach((qAttempts) => {
          qAttempts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          const latest = qAttempts[0];
          attemptedQIds.add(latest.questionId);
          // Only count as failed if is_correct is explicitly false (0 or false)
          // Undefined/null values should not be counted as failed
          const isCorrect = latest.is_correct === 1 || latest.is_correct === true || latest.isCorrect === true;
          const isIncorrect = latest.is_correct === 0 || latest.is_correct === false || latest.isCorrect === false;
          console.log('Question ID:', latest.questionId, 'isCorrect:', isCorrect, 'isIncorrect:', isIncorrect, 'is_correct value:', latest.is_correct);
          if (isIncorrect) failedIds.add(latest.questionId);
        });
        console.log('Failed questions (all references):', failedIds.size);
        console.log('Attempted question IDs:', Array.from(attemptedQIds).slice(0, 5));
        console.log('Failed question IDs:', Array.from(failedIds).slice(0, 5));

        // Fetch ALL questions (Firestore where('reference','==',null) doesn't match missing fields)
        const allQ = query(collection(db, 'questions'), limit(2000));
        const questionsSnap = await getDocs(allQ);
        const fetchedQuestions = questionsSnap.docs.map(doc => doc.data());
        console.log('Total questions fetched:', fetchedQuestions.length);

        // Client-side filter by reference
        const refFilteredQuestions = fetchedQuestions.filter((q: any) => {
          if (selectedReference === 'PMBOK 07') return q.reference === 'PMBOK 07';
          return q.reference === 'PMBOK 08' || !q.reference || q.reference === null || q.reference === undefined;
        });
        console.log('Questions for reference', selectedReference, ':', refFilteredQuestions.length);

        // Filter failed IDs to only include questions for the selected reference
        const referenceFailedIds = new Set<string>();
        refFilteredQuestions.forEach((q: any) => {
          const qId = q.question_id || String(q.id);
          if (failedIds.has(qId)) referenceFailedIds.add(qId);
        });
        console.log('Failed questions for selected reference:', referenceFailedIds.size);
        console.log('Reference failed question IDs:', Array.from(referenceFailedIds).slice(0, 5));
        setOverallFailedIds(referenceFailedIds);

        // Build unanswered set: questions with no attempts at all for the selected reference
        const unansweredIds = new Set<string>();
        refFilteredQuestions.forEach((q: any) => {
          const qId = q.question_id || String(q.id);
          if (!attemptedQIds.has(qId)) unansweredIds.add(qId);
        });
        console.log('Unanswered questions for selected reference:', unansweredIds.size);
        setOverallUnansweredIds(unansweredIds);
      } catch (err) {
        console.error('Failed to load past attempts:', err);
      }
    };
    loadAttempts();
  }, [user?.firebase_uid, selectedReference]);

  // Auto-start exam if navigating from dashboard with target question or failed question IDs
  useEffect(() => {
    if (!started && !loading && (location.state?.targetQuestionId || location.state?.failedQuestionIds) && !autoStartAttempted.current) {
      autoStartAttempted.current = true;
      setMode('training');
      if (location.state?.reference) {
        setSelectedReference(location.state.reference);
      }
      startExam();
    }
  }, [location.state?.targetQuestionId, location.state?.failedQuestionIds, started, loading]);

  // Live per-question timer
  useEffect(() => {
    if (!questionStartAt) return;
    const interval = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(interval);
  }, [questionStartAt]);

  const currentQuestionElapsed = useMemo(() => {
    if (!questionStartAt) return 0;
    return Math.max(1, Math.round((Date.now() - questionStartAt) / 1000) + (timeSpent[currentQuestion?.id || 0] || 0));
  }, [tick, questionStartAt, currentQuestion, timeSpent]);

  // Save progress when the user closes/refreshes the page
  useEffect(() => {
    if (!started) return;
    const handleBeforeUnload = () => saveCurrentProgress();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [started, answers, questions, mode, startedAt, user?.firebase_uid]);

  const shuffleArray = <T,>(arr: T[]): T[] => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const startExam = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use reference from navigation state if available (e.g., from dashboard), otherwise use selectedReference
      const effectiveRef = (location.state?.reference as 'PMBOK 07' | 'PMBOK 08') || selectedReference;
      const needsAttempts = user?.firebase_uid && (practiceFailedOnly || practiceUnansweredOnly || !!location.state?.failedQuestionIds);

      // Build questions query — filter by reference directly on Firestore for PMBOK 07
      const questionsQuery = effectiveRef === 'PMBOK 07'
        ? query(collection(db, 'questions'), where('reference', '==', 'PMBOK 07'), limit(2000))
        : query(collection(db, 'questions'), limit(2000));

      // Build attempts query — only fetch when needed
      const attemptsQueryPromise = needsAttempts
        ? getDocs(query(collection(db, 'attempts'), where('userId', '==', user!.firebase_uid)))
        : Promise.resolve(null);

      // Fetch questions and attempts IN PARALLEL
      const [querySnapshot, attemptsSnap] = await Promise.all([getDocs(questionsQuery), attemptsQueryPromise]);

      console.log('Total documents fetched from Firestore:', querySnapshot.docs.length);
      let fetchedQuestions: Question[] = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const optionsEn = Array.isArray(data.options_en) ? data.options_en : (data.options_en || '').split('|').filter((o: string) => o.trim());
        const optionsAr = Array.isArray(data.options_ar) ? data.options_ar : (data.options_ar || '').split('|').filter((o: string) => o.trim());
        const parseChoiceExpl = (val: any): any[] => {
          if (!val) return [];
          if (Array.isArray(val)) return val;
          if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
          if (typeof val === 'object') return Object.values(val);
          return [];
        };
        const choiceExplEn = parseChoiceExpl(data.choice_explanations_en);
        const choiceExplAr = parseChoiceExpl(data.choice_explanations_ar);
        return {
          id: data.id,
          question_id: data.question_id,
          type: data.type,
          cognitive_type: data.cognitive_type,
          scenario_en: data.scenario_en,
          scenario_ar: data.scenario_ar,
          options_en: optionsEn,
          options_ar: optionsAr,
          domain: data.domain,
          difficulty: data.difficulty,
          reference: data.reference || 'PMBOK 08',
          correct_answer: data.correct_answer,
          explanation_en: data.explanation_en,
          explanation_ar: data.explanation_ar,
          choice_explanations_en: choiceExplEn,
          choice_explanations_ar: choiceExplAr,
        };
      });

      // Client-side filter by reference (needed for PMBOK 08 which has legacy docs without reference field)
      if (effectiveRef !== 'PMBOK 07') {
        fetchedQuestions = fetchedQuestions.filter(q =>
          q.reference === 'PMBOK 08' || !q.reference || q.reference === null || q.reference === undefined
        );
      }

      // Filter by difficulty if selected
      if (selectedDifficulty !== 'all') {
        fetchedQuestions = fetchedQuestions.filter(q => q.difficulty === selectedDifficulty);
      }

      // Filter by domain if selected
      if (selectedDomain !== 'all') {
        fetchedQuestions = fetchedQuestions.filter(q => q.domain === selectedDomain);
      }

      // Filter by type if selected
      if (selectedType !== 'all') {
        fetchedQuestions = fetchedQuestions.filter(q => q.type === selectedType);
      }

      if (fetchedQuestions.length === 0) {
        setError(isRTL ? 'لا توجد أسئلة متاحة للمرجع المختار.' : `No questions available for the selected reference (${effectiveRef}).`);
        setLoading(false);
        return;
      }

      // Process attempts if fetched
      if (user?.firebase_uid && attemptsSnap) {
        try {
          const attempts = attemptsSnap.docs.map(d => d.data()) as any[];

          // Group by questionId, find latest attempt per question
          const attemptsByQ = new Map<string, any[]>();
          attempts.forEach((a: any) => {
            const qId = a.questionId;
            if (!attemptsByQ.has(qId)) attemptsByQ.set(qId, []);
            attemptsByQ.get(qId)!.push(a);
          });

          const failedIds = new Set<string>();
          const attemptedQIds = new Set<string>();
          attemptsByQ.forEach((qAttempts) => {
            qAttempts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            const latest = qAttempts[0];
            attemptedQIds.add(latest.questionId);
            // Only count as failed if is_correct is explicitly false (0 or false)
            const isIncorrect = latest.is_correct === 0 || latest.is_correct === false || latest.isCorrect === false;
            if (isIncorrect) failedIds.add(latest.questionId);
          });

          setOverallFailedIds(failedIds);

          // Build unanswered set: questions with no attempts at all
          const unansweredIds = new Set<string>();
          fetchedQuestions.forEach(q => {
            const qId = q.question_id || String(q.id);
            if (!attemptedQIds.has(qId)) unansweredIds.add(qId);
          });
          setOverallUnansweredIds(unansweredIds);

          // Filter questions based on practice mode
          if (practiceFailedOnly) {
            fetchedQuestions = fetchedQuestions.filter(q => {
              const qId = q.question_id || String(q.id);
              return failedIds.has(qId);
            });
            if (fetchedQuestions.length === 0) {
              setError(isRTL ? 'لا توجد أسئلة فاشلة سابقة. خذ امتحاناً أولاً.' : 'No failed questions found from past attempts. Take an exam first.');
              setLoading(false);
              return;
            }
          } else if (practiceUnansweredOnly) {
            fetchedQuestions = fetchedQuestions.filter(q => {
              const qId = q.question_id || String(q.id);
              return unansweredIds.has(qId);
            });
            if (fetchedQuestions.length === 0) {
              setError(isRTL ? 'لا توجد أسئلة غير مجابة. لقد أجبت على جميع الأسئلة.' : 'No unanswered questions found. You have attempted all questions.');
              setLoading(false);
              return;
            }
          }
        } catch (err) {
          console.error('Failed to load past attempts:', err);
        }
      }

      // Filter to specific failed question IDs if provided from dashboard
      const failedQuestionIds = location.state?.failedQuestionIds as string[] | undefined;
      if (failedQuestionIds && failedQuestionIds.length > 0) {
        const failedSet = new Set(failedQuestionIds);
        fetchedQuestions = fetchedQuestions.filter(q => {
          const qId = q.question_id || String(q.id);
          return failedSet.has(qId);
        });
        if (fetchedQuestions.length === 0) {
          setError(isRTL ? 'لم يتم العثور على الأسئلة الفاشلة للمرجع المختار.' : 'No failed questions found for the selected reference.');
          setLoading(false);
          return;
        }
      }

      // Shuffle questions so users don't see the same order every time
      const shuffledQuestions = shuffleArray(fetchedQuestions);
      setQuestions(shuffledQuestions);
      setStarted(true);

      // Check if navigating from dashboard with target question
      const targetQuestionId = location.state?.targetQuestionId;
      let targetIndex = 0;
      if (targetQuestionId) {
        const foundIndex = shuffledQuestions.findIndex((q: any) => q.question_id === targetQuestionId);
        if (foundIndex >= 0) {
          targetIndex = foundIndex;
        }
      }
      setCurrentIndex(targetIndex);

      setAnswers({});
      setTimeSpent({});
      setShowResults(false);
      setScore(0);
      setResultStats(null);
      setCheckedQuestions(new Set());
      setStartedAt(Date.now());
      setQuestionStartAt(Date.now());
      setCurrentSessionId(`${Date.now()}-${Math.random().toString(16).slice(2)}`);
    } catch (err: any) {
      setError(err.message || 'Failed to load questions from the database. Please log in and try again.');
    } finally {
      setLoading(false);
    }
  };

  const isQuestionChecked = (question: Question) => {
    const trackingId = question.question_id || question.id;
    return checkedQuestions.has(trackingId);
  };

  const recordQuestionTime = (questionId: string) => {
    if (!questionStartAt) return 0;
    const elapsed = Math.max(1, Math.round((Date.now() - questionStartAt) / 1000));
    setTimeSpent((prev) => {
      const prevTime = prev[questionId] || 0;
      return { ...prev, [questionId]: prevTime + elapsed };
    });
    return elapsed;
  };

  const handleCheckAnswer = (question: Question) => {
    if (mode !== 'training') return;
    recordQuestionTime(question.id);
    const trackingId = question.question_id || question.id;
    setCheckedQuestions((prev) => {
      const next = new Set(prev);
      next.add(trackingId);
      return next;
    });
    // Save this question's attempt to Firestore immediately
    saveSingleAttempt(question);
  };

  const canCheckAnswer = (question: Question) => {
    if (mode !== 'training') return false;
    const ans = answers[question.id];
    if (isAnswerEmpty(ans)) return false;
    return !isQuestionChecked(question);
  };

  const handleAnswerChange = (question: Question, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
  };

  const serializeAnswer = (selectedAnswer: AnswerValue | undefined): string => {
    if (selectedAnswer === null || selectedAnswer === undefined || selectedAnswer === '') return '';
    if (Array.isArray(selectedAnswer)) return selectedAnswer.join(',');
    if (typeof selectedAnswer === 'number') return String(selectedAnswer);
    if (typeof selectedAnswer === 'object') return JSON.stringify(selectedAnswer);
    return String(selectedAnswer);
  };

  const calculateCurrentStats = () => {
    let correct = 0;
    let incorrect = 0;
    let unanswered = 0;

    questions.forEach((q) => {
      const selectedAnswer = answers[q.id];
      if (isAnswerEmpty(selectedAnswer)) {
        unanswered++;
      } else if (isCorrectAnswer(q, selectedAnswer)) {
        correct++;
      } else {
        incorrect++;
      }
    });

    const finalScore = questions.length > 0 ? Math.round((correct / questions.length) * 100) : 0;
    const durationSeconds = Math.max(1, Math.round((Date.now() - (startedAt || Date.now())) / 1000));
    const answeredQuestionIds = questions.filter((q) => !isAnswerEmpty(answers[q.id])).map((q) => q.id);
    const answeredCount = answeredQuestionIds.length;
    const totalAnsweredSeconds = answeredCount > 0
      ? answeredQuestionIds.reduce((sum, id) => sum + (timeSpent[id] || Math.round(durationSeconds / (questions.length || 1))), 0)
      : durationSeconds;
    const averageSecondsPerQuestion = answeredCount > 0 ? Math.round(totalAnsweredSeconds / answeredCount) : 0;

    return { correct, incorrect, unanswered, score: finalScore, durationSeconds, averageSecondsPerQuestion };
  };

  const saveSingleAttempt = async (question: Question, overrideAnswer?: AnswerValue) => {
    if (!user?.firebase_uid || !currentSessionId) return;
    const selectedAnswer = overrideAnswer ?? answers[question.id];
    if (isAnswerEmpty(selectedAnswer)) return;
    const isCorrect = isCorrectAnswer(question, selectedAnswer);
    await addDoc(collection(db, 'attempts'), {
      userId: user.firebase_uid,
      questionId: question.question_id || question.id,
      selectedAnswer: serializeAnswer(selectedAnswer),
      isCorrect,
      domain: question.domain || 'Unknown',
      timeSpent: timeSpent[question.id] || 0,
      sessionId: currentSessionId,
      reference: selectedReference,
      created_at: new Date().toISOString(),
    }).catch(console.error);
  };

  const saveCurrentProgress = async () => {
    if (questions.length === 0 || !currentSessionId || !user?.firebase_uid) return;
    // Save all answered questions as attempts to Firestore
    const attemptPromises = questions.map((q) => {
      const selectedAnswer = answers[q.id];
      if (isAnswerEmpty(selectedAnswer)) return Promise.resolve();
      const isCorrect = isCorrectAnswer(q, selectedAnswer);
      return addDoc(collection(db, 'attempts'), {
        userId: user.firebase_uid,
        questionId: q.question_id || q.id,
        selectedAnswer: serializeAnswer(selectedAnswer),
        isCorrect,
        domain: q.domain || 'Unknown',
        timeSpent: timeSpent[q.id] || 0,
        sessionId: currentSessionId,
        reference: selectedReference,
        created_at: new Date().toISOString(),
      }).catch(console.error);
    });
    await Promise.all(attemptPromises);
  };

  const finishExam = async () => {
    console.log('finishExam called');
    try {
      if (questions[currentIndex]) recordQuestionTime(questions[currentIndex].id);
      const stats = calculateCurrentStats();
      console.log('Stats:', stats);
      // Save individual question attempts for all modes
      if (user?.firebase_uid) {
        const attemptPromises = questions.map((q) => {
          const selectedAnswer = answers[q.id];
          if (isAnswerEmpty(selectedAnswer)) return Promise.resolve();
          const isCorrect = isCorrectAnswer(q, selectedAnswer);
          return addDoc(collection(db, 'attempts'), {
            userId: user.firebase_uid,
            questionId: q.question_id || q.id,
            selectedAnswer: serializeAnswer(selectedAnswer),
            isCorrect,
            domain: q.domain || 'Unknown',
            timeSpent: timeSpent[q.id] || 0,
            sessionId: currentSessionId,
            reference: selectedReference,
            created_at: new Date().toISOString(),
          }).catch(console.error);
        });
        await Promise.all(attemptPromises);
        console.log('Attempts saved');

        // Save exam session
        await addDoc(collection(db, 'exam_sessions'), {
          userId: user.firebase_uid,
          session_id: currentSessionId || '',
          mode: mode,
          total_questions: questions.length,
          correct: stats.correct,
          incorrect: stats.incorrect,
          unanswered: stats.unanswered,
          score: stats.score,
          duration_seconds: stats.durationSeconds,
          average_seconds_per_question: stats.averageSecondsPerQuestion,
          reference: selectedReference,
          created_at: new Date().toISOString(),
        }).catch(console.error);
        console.log('Exam session saved');
      }
      setScore(stats.score);
      setResultStats({ correct: stats.correct, incorrect: stats.incorrect, unanswered: stats.unanswered, averageSecondsPerQuestion: stats.averageSecondsPerQuestion });
      setShowResults(true);
      console.log('Results shown');
    } catch (error) {
      console.error('Error in finishExam:', error);
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  if (!started) {
    const activeFilters: string[] = [];
    if (practiceFailedOnly) activeFilters.push(isRTL ? 'الأسئلة الفاشلة' : 'Failed Questions');
    if (practiceUnansweredOnly) activeFilters.push(isRTL ? 'الأسئلة غير المجابة' : 'Unanswered Questions');

    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/30">
        <div className="w-full max-w-5xl mx-auto">
          {/* ── Premium Launch Card ── */}
          <div className="relative rounded-3xl border border-border/60 bg-card/80 backdrop-blur-xl shadow-2xl shadow-blue-500/5 dark:shadow-black/20 overflow-hidden">
            {/* Decorative gradient orb */}
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-400/10 dark:from-blue-500/10 dark:to-purple-500/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-gradient-to-br from-emerald-400/15 to-cyan-400/10 dark:from-emerald-500/8 dark:to-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative p-6 sm:p-10 lg:p-12">
              {/* ═══ SECTION 1 — HERO ═══ */}
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-semibold mb-5">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                  {isRTL ? 'محاكي اختبار PMP' : 'PMP Exam Simulator'}
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight bg-gradient-to-r from-slate-900 via-blue-800 to-indigo-700 dark:from-white dark:via-blue-100 dark:to-indigo-200 bg-clip-text text-transparent mb-4">
                  {isRTL ? 'ابدأ جلسة اختبار PMP' : 'Start Your PMP Session'}
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto mb-6">
                  {isRTL ? 'اختر وضعك المفضل وابدأ رحلة التحضير لاختبار PMP.' : 'Choose your preferred mode and begin your PMP preparation journey.'}
                </p>
                {/* Trust badges */}
                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                  {[
                    { label: 'PMI ECO 2026', icon: '✓' },
                    { label: isRTL ? 'متوافق مع PMBOK' : 'PMBOK Aligned', icon: '✓' },
                    { label: isRTL ? 'عربي وإنجليزي' : 'Arabic & English', icon: '✓' },
                    { label: isRTL ? 'تحليلات ذكية' : 'AI Analytics', icon: '✓' },
                  ].map((badge) => (
                    <span key={badge.label} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs font-medium">
                      <span className="text-emerald-500">{badge.icon}</span>
                      {badge.label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Error banner */}
              {error && (
                <div className="mb-6 p-4 rounded-2xl border border-red-200 bg-red-50/80 dark:bg-red-950/30 dark:border-red-800 text-red-800 dark:text-red-300 text-sm text-center">
                  {error}
                </div>
              )}

              {/* ═══ SECTION 2 — MODE SELECTION ═══ */}
              <div className="mb-8">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 text-center">
                  {isRTL ? 'اختر الوضع' : 'Select Mode'}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Training Mode Card */}
                  <button
                    onClick={() => setMode('training')}
                    className={`group relative p-6 rounded-2xl border-2 text-left transition-all duration-300 overflow-hidden ${
                      mode === 'training'
                        ? 'border-blue-500 bg-gradient-to-br from-blue-500/10 to-indigo-500/5 dark:from-blue-500/15 dark:to-indigo-500/5 shadow-lg shadow-blue-500/10 scale-[1.02]'
                        : 'border-border bg-card/50 hover:border-blue-400/50 hover:bg-accent/30'
                    }`}
                  >
                    {mode === 'training' && (
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none" />
                    )}
                    <div className="relative flex items-start gap-4">
                      <div className={`flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl transition-colors ${
                        mode === 'training'
                          ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                          : 'bg-blue-500/10 text-blue-600 dark:text-blue-400 group-hover:bg-blue-500/20'
                      }`}>
                        🎓
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold mb-1">{t('exam.trainingMode')}</h3>
                        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                          {isRTL ? 'تدرب مع تغذية راجعة فورية وشروحات ودعم تعليمي.' : 'Practice with instant feedback, explanations, and learning support.'}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {[
                            isRTL ? 'شروحات' : 'Explanations',
                            isRTL ? 'تلميحات ذكية' : 'AI Hints',
                            isRTL ? 'بدون ضغط' : 'No Exam Pressure',
                          ].map((feat) => (
                            <span key={feat} className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                              <span className="text-emerald-500">✓</span> {feat}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {mode === 'training' && (
                      <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold shadow-lg">
                        ✓
                      </div>
                    )}
                  </button>

                  {/* Exam Mode Card */}
                  <button
                    onClick={() => setMode('exam')}
                    className={`group relative p-6 rounded-2xl border-2 text-left transition-all duration-300 overflow-hidden ${
                      mode === 'exam'
                        ? 'border-violet-500 bg-gradient-to-br from-violet-500/10 to-purple-500/5 dark:from-violet-500/15 dark:to-purple-500/5 shadow-lg shadow-violet-500/10 scale-[1.02]'
                        : 'border-border bg-card/50 hover:border-violet-400/50 hover:bg-accent/30'
                    }`}
                  >
                    {mode === 'exam' && (
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-violet-500/5 to-purple-500/5 pointer-events-none" />
                    )}
                    <div className="relative flex items-start gap-4">
                      <div className={`flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl transition-colors ${
                        mode === 'exam'
                          ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/30'
                          : 'bg-violet-500/10 text-violet-600 dark:text-violet-400 group-hover:bg-violet-500/20'
                      }`}>
                        📋
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold mb-1">{t('exam.examMode')}</h3>
                        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                          {isRTL ? 'محاكاة بيئة اختبار PMP الحقيقية مع التوقيت والتصحيح.' : 'Simulate the real PMP exam environment with timing and scoring.'}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {[
                            isRTL ? 'إحساس حقيقي' : 'Real Exam Feel',
                            isRTL ? 'درجة نهائية' : 'Final Score',
                            isRTL ? 'تقرير الأداء' : 'Performance Report',
                          ].map((feat) => (
                            <span key={feat} className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-600 dark:text-violet-400">
                              <span className="text-violet-500">✓</span> {feat}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {mode === 'exam' && (
                      <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-violet-500 text-white flex items-center justify-center text-xs font-bold shadow-lg">
                        ✓
                      </div>
                    )}
                  </button>
                </div>
              </div>

              {/* ═══ SECTION 3 — PRACTICE FILTERS (Training only) ═══ */}
              {/* Reference selector */}
              <div className="mb-8">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 text-center">
                  {isRTL ? 'المرجع' : 'Reference'}
                </h2>
                <div className="flex justify-center gap-3">
                  {['PMBOK 08', 'PMBOK 07'].map((ref) => (
                    <button
                      key={ref}
                      onClick={() => setSelectedReference(ref as 'PMBOK 08' | 'PMBOK 07')}
                      className={`px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-all ${
                        selectedReference === ref
                          ? 'border-primary bg-primary text-primary-foreground shadow-lg'
                          : 'border-border bg-card/50 text-muted-foreground hover:bg-accent/30'
                      }`}
                    >
                      {ref}
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty selector */}
              <div className="mb-8">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 text-center">
                  {isRTL ? 'مستوى الصعوبة' : 'Difficulty Level'}
                </h2>
                <div className="flex flex-wrap justify-center gap-2">
                  {[
                    { value: 'all', label: isRTL ? 'الكل' : 'All' },
                    { value: 'Hard', label: isRTL ? 'صعب' : 'Hard' },
                    { value: 'Expert', label: isRTL ? 'خبير' : 'Expert' },
                    { value: 'Hyper-Expert', label: isRTL ? 'خبير فائق' : 'Hyper-Expert' },
                  ].map((diff) => {
                    const isSelected = selectedDifficulty === diff.value;
                    let activeClass = '';
                    if (isSelected) {
                      if (diff.value === 'all') activeClass = 'border-slate-500 bg-slate-500 text-white shadow-lg';
                      else if (diff.value === 'Hard') activeClass = 'border-blue-500 bg-blue-500 text-white shadow-lg';
                      else if (diff.value === 'Expert') activeClass = 'border-amber-500 bg-amber-500 text-white shadow-lg';
                      else if (diff.value === 'Hyper-Expert') activeClass = 'border-rose-500 bg-rose-500 text-white shadow-lg';
                    }
                    return (
                      <button
                        key={diff.value}
                        onClick={() => setSelectedDifficulty(diff.value as 'all' | 'Hard' | 'Expert' | 'Hyper-Expert')}
                        className={`px-4 py-2 rounded-xl border-2 text-sm font-bold transition-all ${
                          isSelected ? activeClass : 'border-border bg-card/50 text-muted-foreground hover:bg-accent/30'
                        }`}
                      >
                        {diff.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Domain selector */}
              <div className="mb-8">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 text-center">
                  {isRTL ? 'مجال المعرفة' : 'Domain'}
                </h2>
                <div className="flex flex-wrap justify-center gap-2">
                  {[
                    { value: 'all', label: isRTL ? 'الكل' : 'All' },
                    { value: 'People', label: isRTL ? 'الأشخاص' : 'People' },
                    { value: 'Process', label: isRTL ? 'العمليات' : 'Process' },
                    { value: 'Business Environment', label: isRTL ? 'بيئة الأعمال' : 'Business Environment' },
                  ].map((dom) => {
                    const isSelected = selectedDomain === dom.value;
                    let activeClass = '';
                    if (isSelected) {
                      if (dom.value === 'all') activeClass = 'border-slate-500 bg-slate-500 text-white shadow-lg';
                      else if (dom.value === 'People') activeClass = 'border-emerald-500 bg-emerald-500 text-white shadow-lg';
                      else if (dom.value === 'Process') activeClass = 'border-violet-500 bg-violet-500 text-white shadow-lg';
                      else if (dom.value === 'Business Environment') activeClass = 'border-orange-500 bg-orange-500 text-white shadow-lg';
                    }
                    return (
                      <button
                        key={dom.value}
                        onClick={() => setSelectedDomain(dom.value as 'all' | 'People' | 'Process' | 'Business Environment')}
                        className={`px-4 py-2 rounded-xl border-2 text-sm font-bold transition-all ${
                          isSelected ? activeClass : 'border-border bg-card/50 text-muted-foreground hover:bg-accent/30'
                        }`}
                      >
                        {dom.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Question Type selector */}
              <div className="mb-8">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 text-center">
                  {isRTL ? 'نوع السؤال' : 'Question Type'}
                </h2>
                <div className="flex flex-wrap justify-center gap-2">
                  {[
                    { value: 'all', label: isRTL ? 'الكل' : 'All' },
                    { value: 'standard', label: isRTL ? 'قياسي' : 'Standard' },
                  ].map((qType) => {
                    const isSelected = selectedType === qType.value;
                    let activeClass = '';
                    if (isSelected) {
                      if (qType.value === 'all') activeClass = 'border-slate-500 bg-slate-500 text-white shadow-lg';
                      else if (qType.value === 'standard') activeClass = 'border-indigo-500 bg-indigo-500 text-white shadow-lg';
                    }
                    return (
                      <button
                        key={qType.value}
                        onClick={() => setSelectedType(qType.value as 'all' | 'standard')}
                        className={`px-4 py-2 rounded-xl border-2 text-sm font-bold transition-all ${
                          isSelected ? activeClass : 'border-border bg-card/50 text-muted-foreground hover:bg-accent/30'
                        }`}
                      >
                        {qType.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {mode === 'training' && (
                <div className="mb-8 animate-in fade-in duration-300">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 text-center">
                    {isRTL ? 'مرشحات التدريب' : 'Practice Filters'}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {/* Failed Questions Toggle */}
                    <button
                      onClick={() => {
                        setPracticeFailedOnly(!practiceFailedOnly);
                        if (!practiceFailedOnly) setPracticeUnansweredOnly(false);
                      }}
                      className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left ${
                        practiceFailedOnly
                          ? 'border-orange-400/60 bg-orange-500/8 dark:bg-orange-500/10'
                          : 'border-border bg-card/40 hover:bg-accent/30'
                      }`}
                    >
                      <div className={`flex-shrink-0 w-12 h-7 rounded-full transition-colors duration-200 relative ${
                        practiceFailedOnly ? 'bg-orange-500' : 'bg-muted-foreground/30'
                      }`}>
                        <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200 ${
                          practiceFailedOnly ? 'translate-x-5' : 'translate-x-0.5'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-bold">{isRTL ? 'الأسئلة الفاشلة' : 'Failed Questions'}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 font-medium">{overallFailedIds.size}</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {isRTL ? 'تدرب على الأسئلة التي أجبت عليها خطأ في الجلسات السابقة.' : 'Practice questions answered incorrectly in previous sessions.'}
                        </p>
                      </div>
                    </button>

                    {/* Unanswered Questions Toggle */}
                    <button
                      onClick={() => {
                        setPracticeUnansweredOnly(!practiceUnansweredOnly);
                        if (!practiceUnansweredOnly) setPracticeFailedOnly(false);
                      }}
                      className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left ${
                        practiceUnansweredOnly
                          ? 'border-amber-400/60 bg-amber-500/8 dark:bg-amber-500/10'
                          : 'border-border bg-card/40 hover:bg-accent/30'
                      }`}
                    >
                      <div className={`flex-shrink-0 w-12 h-7 rounded-full transition-colors duration-200 relative ${
                        practiceUnansweredOnly ? 'bg-amber-500' : 'bg-muted-foreground/30'
                      }`}>
                        <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200 ${
                          practiceUnansweredOnly ? 'translate-x-5' : 'translate-x-0.5'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-bold">{isRTL ? 'الأسئلة غير المجابة' : 'Unanswered Questions'}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">{overallUnansweredIds.size}</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {isRTL ? 'تدرب على الأسئلة التي لم تحاولها من قبل.' : 'Practice questions not attempted previously.'}
                        </p>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* ═══ SECTION 4 — SESSION SUMMARY ═══ */}
              <div className="mb-8">
                <div className="rounded-2xl border border-border/60 bg-muted/20 dark:bg-muted/10 p-4 sm:p-5">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                    {isRTL ? 'ملخص الجلسة' : 'Session Summary'}
                  </h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-sm">🗄️</div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{isRTL ? 'المصدر' : 'Source'}</div>
                        <div className="text-xs font-semibold truncate">{isRTL ? 'قاعدة البيانات' : 'Database'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-sm">🌐</div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{isRTL ? 'اللغة' : 'Language'}</div>
                        <div className="text-xs font-semibold truncate">{isRTL ? 'عربي / إنجليزي' : 'Arabic / English'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${mode === 'training' ? 'bg-blue-500/10' : 'bg-violet-500/10'}`}>
                        {mode === 'training' ? '🎓' : '📋'}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{isRTL ? 'الوضع' : 'Mode'}</div>
                        <div className="text-xs font-semibold truncate">{mode === 'training' ? (isRTL ? 'تدريب' : 'Training') : (isRTL ? 'اختبار' : 'Exam')}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-sm">⚡</div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{isRTL ? 'المرشحات' : 'Filters'}</div>
                        <div className="text-xs font-semibold truncate">
                          {activeFilters.length > 0 ? activeFilters.join(', ') : (isRTL ? 'الكل' : 'All Questions')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ═══ SECTION 5 — PRIMARY CTA ═══ */}
              <button
                onClick={startExam}
                disabled={loading}
                className={`w-full py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group ${
                  mode === 'training'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-xl shadow-blue-500/20 hover:shadow-blue-500/30 hover:scale-[1.01]'
                    : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-xl shadow-violet-500/20 hover:shadow-violet-500/30 hover:scale-[1.01]'
                }`}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      {mode === 'training'
                        ? (isRTL ? 'ابدأ جلسة التدريب' : 'Start Training Session')
                        : (isRTL ? 'ابدأ اختبار PMP التجريبي' : 'Start PMP Mock Exam')}
                      <span className="text-xl">→</span>
                    </>
                  )}
                </span>
              </button>

              {/* Footer hint */}
              <p className="text-center text-xs text-muted-foreground mt-5">
                {t('exam.questionsFromDB')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <h1 className="text-3xl font-bold mb-4">{t('exam.results')}</h1>
        <div className="text-6xl font-bold text-primary mb-4">{score}%</div>
        {resultStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 text-left">
            <div className="p-4 border rounded-lg bg-card">
              <div className="text-2xl font-bold text-green-600">{resultStats.correct}</div>
              <div className="text-xs text-muted-foreground">{t('exam.successful')}</div>
            </div>
            <div className="p-4 border rounded-lg bg-card">
              <div className="text-2xl font-bold text-destructive">{resultStats.incorrect}</div>
              <div className="text-xs text-muted-foreground">{t('exam.failed')}</div>
            </div>
            <div className="p-4 border rounded-lg bg-card">
              <div className="text-2xl font-bold">{resultStats.unanswered}</div>
              <div className="text-xs text-muted-foreground">{t('exam.unanswered')}</div>
            </div>
            <div className="p-4 border rounded-lg bg-card">
              <div className="text-2xl font-bold">{formatDuration(resultStats.averageSecondsPerQuestion)}</div>
              <div className="text-xs text-muted-foreground">{t('exam.avgPerQuestion')}</div>
            </div>
          </div>
        )}
        <div className="flex gap-4 justify-center mb-8">
          <button onClick={() => { setStarted(false); setShowResults(false); setResultStats(null); setCheckedQuestions(new Set()); setCurrentSessionId(null); }} className="px-6 py-3 bg-primary text-primary-foreground rounded-lg">
            {t('exam.retake')}
          </button>
          <Link to="/dashboard" className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity">
            {t('exam.viewDashboard')}
          </Link>
        </div>
      </div>
    );
  }

  // Debug logging for missing content
  if (currentQuestion) {
    console.log('Current Question Debug:', {
      id: currentQuestion.id,
      question_id: currentQuestion.question_id,
      difficulty: currentQuestion.difficulty,
      hasScenarioEn: !!currentQuestion.scenario_en,
      hasScenarioAr: !!currentQuestion.scenario_ar,
      hasOptionsEn: !!currentQuestion.options_en?.length,
      hasOptionsAr: !!currentQuestion.options_ar?.length,
      hasExplanationEn: !!currentQuestion.explanation_en,
      hasExplanationAr: !!currentQuestion.explanation_ar,
      scenarioEnLength: currentQuestion.scenario_en?.length || 0,
      scenarioArLength: currentQuestion.scenario_ar?.length || 0,
      fullQuestion: currentQuestion,
    });
  }

  const correctCount = questions.filter((q) => isQuestionChecked(q) && isCorrectAnswer(q, answers[q.id])).length;
  const incorrectCount = questions.filter((q) => isQuestionChecked(q) && !isCorrectAnswer(q, answers[q.id])).length;

  const unansweredCount = questions.filter((q) => isAnswerEmpty(answers[q.id])).length;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6 bg-card border rounded-xl px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (mode === 'training' && Object.keys(answers).length > 0) {
                setShowExitWarning(true);
              } else {
                saveCurrentProgress();
                setStarted(false);
              }
            }}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            aria-label="Save and exit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
            {mode === 'training' ? t('exam.trainingMode') : t('exam.examMode')}
          </span>
          <span className="text-sm text-muted-foreground">
            Q {currentIndex + 1} / {questions.length}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-sm font-medium">
            <span className="text-green-600">{correctCount}</span>
            <span className="text-muted-foreground">/</span>
            <span>{questions.length}</span>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-sm font-medium px-2 py-1 bg-muted rounded-lg">
            <span>⏱</span>
            <span>{formatDuration(currentQuestionElapsed)}</span>
          </div>
          {mode === 'exam' && <ExamTimer durationMinutes={60} onTimeUp={finishExam} />}
          {mode === 'training' && (
            <button
              onClick={() => handleCheckAnswer(currentQuestion)}
              disabled={!canCheckAnswer(currentQuestion)}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50 font-medium"
            >
              {t('exam.check')}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main question card */}
        {currentQuestion && (
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card border rounded-xl p-6 shadow-sm">
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {currentQuestion.question_id && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium font-mono">
                    {currentQuestion.question_id}
                  </span>
                )}
                {currentQuestion.reference && (
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                    {currentQuestion.reference}
                  </span>
                )}
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                  {currentQuestion.domain}
                </span>
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium capitalize">
                  {currentQuestion.type?.replace('_', ' ') || 'MCQ'}
                </span>
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium capitalize">
                  {currentQuestion.difficulty}
                </span>
                {currentQuestion.cognitive_type && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium capitalize">
                    {currentQuestion.cognitive_type.replace('_', ' ')}
                  </span>
                )}
              </div>

              {/* Scenario header */}
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('exam.scenario')}
                </h3>
                <div className="flex bg-muted rounded-lg p-1">
                  <button
                    onClick={() => i18n.changeLanguage('en')}
                    className={`px-3 py-1 text-xs rounded transition-all ${!isRTL ? 'bg-white shadow-sm font-medium' : 'text-muted-foreground'}`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => i18n.changeLanguage('ar')}
                    className={`px-3 py-1 text-xs rounded transition-all ${isRTL ? 'bg-white shadow-sm font-medium' : 'text-muted-foreground'}`}
                  >
                    AR
                  </button>
                </div>
              </div>

              {/* Scenario text */}
              <p className="text-lg leading-relaxed mb-6">
                {isRTL ? (currentQuestion.scenario_ar || currentQuestion.scenario_en) : currentQuestion.scenario_en}
              </p>

              {/* Question prompt based on type */}
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                {getQuestionTypeLabel(currentQuestion.type, t)}
              </h3>

              {/* Question interaction renderer */}
              <QuestionRenderer
                key={currentQuestion.id}
                question={currentQuestion}
                answer={answers[currentQuestion.id] ?? ''}
                isRTL={isRTL}
                isChecked={isQuestionChecked(currentQuestion)}
                mode={mode}
                disabled={mode === 'exam' && isQuestionChecked(currentQuestion)}
                onAnswerChange={(value) => handleAnswerChange(currentQuestion, value)}
              />

              {/* Check Answer button in training mode */}
              {canCheckAnswer(currentQuestion) && (
                <button
                  onClick={() => handleCheckAnswer(currentQuestion)}
                  className="mt-4 w-full px-4 py-3 bg-amber-500 text-white rounded-lg hover:opacity-90 font-medium"
                >
                  {t('exam.checkAnswer')}
                </button>
              )}

              {/* v2 layout: Nav → Dashboard → Explanation */}
              {/* Navigation */}
              <div className="flex justify-between mt-4">
                <button
                  onClick={() => {
                    if (currentQuestion) recordQuestionTime(currentQuestion.id);
                    setCurrentIndex((p) => Math.max(0, p - 1));
                    setQuestionStartAt(Date.now());
                  }}
                  disabled={currentIndex === 0}
                  className="px-4 py-2 border rounded-lg hover:bg-accent disabled:opacity-50"
                >
                  {t('exam.prev')}
                </button>

                {mode === 'training' && !isQuestionChecked(currentQuestion) && currentIndex < questions.length - 1 ? (
                  <button
                    disabled
                    className="px-4 py-2 border rounded-lg opacity-50"
                  >
                    {t('exam.answerToContinue')}
                  </button>
                ) : currentIndex < questions.length - 1 ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (currentQuestion) recordQuestionTime(currentQuestion.id);
                        setCurrentIndex((p) => p + 1);
                        setQuestionStartAt(Date.now());
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                    >
                      {t('exam.next')}
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={finishExam}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:opacity-90 font-medium"
                    >
                      {t('exam.submit')}
                    </button>
                  </div>
                )}
              </div>

              {/* Save & Dashboard buttons for training mode */}
              {mode === 'training' && (
                <div className="flex gap-3 justify-center mt-4 pt-4 border-t-2">
                  <button
                    onClick={async () => {
                      const stats = calculateCurrentStats();
                      if (user?.firebase_uid) {
                        await addDoc(collection(db, 'exam_sessions'), {
                          userId: user.firebase_uid,
                          session_id: currentSessionId || '',
                          mode: mode,
                          total_questions: questions.length,
                          correct: stats.correct,
                          incorrect: stats.incorrect,
                          unanswered: stats.unanswered,
                          score: stats.score,
                          duration_seconds: stats.durationSeconds,
                          average_seconds_per_question: stats.averageSecondsPerQuestion,
                          reference: selectedReference,
                          created_at: new Date().toISOString(),
                        }).catch(console.error);
                      }
                      navigate('/dashboard');
                    }}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:opacity-90 font-medium"
                  >
                    {t('exam.saveAndViewDashboard')}
                  </button>
                </div>
              )}

              {/* Explanation in training mode after check */}
              {mode === 'training' && isQuestionChecked(currentQuestion) && (
                <div className="mt-6 p-4 rounded-lg bg-muted/40 border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {isCorrectAnswer(currentQuestion, answers[currentQuestion.id]) ? (
                        <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-700 rounded">{t('exam.correctExcl')}</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-700 rounded">{t('exam.incorrectLabel')}</span>
                      )}
                      <span className="text-xs text-muted-foreground">{t('exam.explanation')}</span>
                    </div>
                    <button
                      onClick={() => {
                        const trackingId = currentQuestion.question_id || currentQuestion.id;
                        setCheckedQuestions((prev) => {
                          const next = new Set(prev);
                          next.delete(trackingId);
                          return next;
                        });
                        setAnswers((prev) => {
                          const next = { ...prev };
                          delete next[currentQuestion.id];
                          return next;
                        });
                      }}
                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    >
                      {t('exam.retry')}
                    </button>
                  </div>
                  <div className="mb-3 p-3 rounded bg-green-50/50 border border-green-200 text-sm">
                    <span className="font-medium text-green-800">{t('exam.correctAnswer')}</span>
                    <span className="text-green-900">{formatCorrectAnswer(currentQuestion)}</span>
                  </div>
                  <p className="text-sm leading-relaxed mb-4">{isRTL ? (currentQuestion.explanation_ar || currentQuestion.explanation_en) : currentQuestion.explanation_en}</p>
                  {currentQuestion.options_en?.length > 0 && (
                    <div className="border-t pt-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('exam.choiceBreakdown')}</p>
                      <div className="space-y-2">
                        {(isRTL ? (currentQuestion.options_ar?.length ? currentQuestion.options_ar : currentQuestion.options_en) : currentQuestion.options_en).map((opt: string, idx: number) => {
                          const correct = isCorrectOption(currentQuestion, opt);
                          const explanation = isRTL
                            ? currentQuestion.choice_explanations_ar?.[idx] || currentQuestion.choice_explanations_en?.[idx] || ''
                            : currentQuestion.choice_explanations_en?.[idx] || currentQuestion.choice_explanations_ar?.[idx];
                          if (!explanation) return null;
                          return (
                            <div key={idx} className={`text-xs p-2 rounded border ${correct ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
                              <span className="font-bold">{correct ? t('exam.correctExcl') : t('exam.incorrectLabel')} — {opt}: </span>
                              {explanation}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress panel — collapsible */}
        <div className="bg-card border rounded-xl shadow-sm h-fit overflow-hidden">
          <button
            onClick={() => setIsProgressCollapsed(!isProgressCollapsed)}
            className="w-full flex items-center justify-between p-4 hover:bg-accent/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">{t('exam.progress')}</h3>
              <span className="text-xs text-muted-foreground">{progressFilter === 'all' ? t('common.all') : progressFilter === 'incorrect' ? t('exam.incorrectLabel') : progressFilter === 'unanswered' ? t('exam.notAnswered') : (isRTL ? 'فاشلة' : 'Failed')}</span>
            </div>
            <svg
              className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${isProgressCollapsed ? '' : 'rotate-180'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {!isProgressCollapsed && (
            <div className="px-6 pb-6">
          {/* Filter buttons */}
          <div className="flex gap-2 mb-4">
            {(['all', 'incorrect', 'unanswered', 'failed'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setProgressFilter(filter)}
                className={`flex-1 px-2 py-1.5 text-xs rounded-lg border transition-colors capitalize ${
                  progressFilter === filter
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'hover:bg-accent'
                }`}
              >
                {filter === 'all' ? t('common.all') : filter === 'incorrect' ? t('exam.incorrectLabel') : filter === 'unanswered' ? t('exam.notAnswered') : (isRTL ? 'فاشلة' : 'Failed')}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-5 gap-2 mb-6">
            {questions.map((q, idx) => {
              const isCurrent = idx === currentIndex;
              const isAnswered = !isAnswerEmpty(answers[q.id]);
              const isChecked = isQuestionChecked(q);
              const isCorrect = isChecked && isCorrectAnswer(q, answers[q.id]);
              const isIncorrect = isChecked && !isCorrectAnswer(q, answers[q.id]);

              const isOverallFailed = overallFailedIds.has(q.question_id || String(q.id));

              let status: 'correct' | 'incorrect' | 'unanswered' | 'answered' | 'failed' = 'unanswered';
              if (isCorrect) status = 'correct';
              else if (isIncorrect) status = 'incorrect';
              else if (isAnswered) status = 'answered';
              // Only mark as 'failed' if it was answered in current session AND is incorrect AND was previously failed
              // Unanswered questions should remain 'unanswered' regardless of past failure status
              if (isOverallFailed && (status === 'incorrect' || status === 'answered')) status = 'failed';

              const isVisible =
                progressFilter === 'all' ||
                (progressFilter === 'incorrect' && status === 'incorrect') ||
                (progressFilter === 'unanswered' && (status === 'unanswered' || status === 'answered')) ||
                (progressFilter === 'failed' && isOverallFailed);

              let btnClass = 'w-10 h-10 rounded-lg text-sm font-medium transition-colors ';
              if (isCurrent) {
                btnClass += 'bg-primary text-primary-foreground';
              } else if (status === 'correct') {
                btnClass += 'bg-green-100 text-green-700';
              } else if (status === 'incorrect' || status === 'failed') {
                btnClass += 'bg-red-100 text-red-700';
              } else if (status === 'answered') {
                btnClass += 'bg-blue-100 text-blue-700';
              } else {
                btnClass += 'bg-muted text-muted-foreground';
              }

              return (
                <button
                  key={q.id}
                  onClick={() => setCurrentIndex(idx)}
                  className={`${btnClass} ${!isVisible ? 'opacity-30 pointer-events-none' : ''}`}
                  title={status === 'correct' ? 'Correct' : status === 'incorrect' ? 'Incorrect' : status === 'failed' ? (isRTL ? 'فاشلة سابقاً' : 'Previously failed') : status === 'answered' ? 'Not checked' : 'Not answered'}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-green-600 font-medium">{t('exam.correct')}</span>
              <span className="font-bold text-lg">{correctCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-red-600 font-medium">{t('exam.incorrectLabel')}</span>
              <span className="font-bold text-lg">{incorrectCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">{t('exam.unanswered')}</span>
              <span className="font-bold text-lg">{unansweredCount}/{questions.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-orange-600 font-medium">{isRTL ? 'إجمالي الفاشلة' : 'Overall Failed'}</span>
              <span className="font-bold text-lg text-orange-600">{overallFailedIds.size}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-amber-600 font-medium">{isRTL ? 'إجمالي غير المجابة' : 'Overall Unanswered'}</span>
              <span className="font-bold text-lg text-amber-600">{overallUnansweredIds.size}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">{t('exam.current')}</span>
              <span className="font-bold text-lg">{formatDuration(currentQuestionElapsed)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">{t('exam.benchmark')}</span>
              <span className="font-bold text-lg">2:17</span>
            </div>
          </div>
            </div>
          )}
        </div>
      </div>

      {/* Exit warning modal */}
      {showExitWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border rounded-xl p-6 max-w-md w-full mx-4 shadow-lg">
            <h3 className="text-lg font-semibold mb-2">{t('exam.unsavedProgress')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('exam.unsavedWarning')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowExitWarning(false)}
                className="px-4 py-2 border rounded-lg hover:bg-accent"
              >
                {t('exam.continueTraining')}
              </button>
              <button
                onClick={() => {
                  setShowExitWarning(false);
                  saveCurrentProgress();
                  setStarted(false);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:opacity-90"
              >
                {t('exam.exitWithoutSaving')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


